import React, { useState, useEffect } from 'react';
import { useStore } from '../../store';
import { useShallow } from 'zustand/react/shallow';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
} from '@roger/ui-components';
import { Loader2, Check, Edit } from 'lucide-react';
import { updateAgency } from '../../services/api';
import type { HnpUploadTab } from '../../services/api/types.gen';

const HNPUPLOADTAB_VALUES: HnpUploadTab[] = [
  'nursing',
  'therapy',
  'pt',
  'ot',
  'st',
  'misc',
  'orders',
];

interface AgencyManagementSectionProps {
  mode: 'readonly' | 'editable';
}

const AgencyManagementSection: React.FC<AgencyManagementSectionProps> = ({
  mode,
}) => {
  const { user, showSuccess, showError, checkAuthStatus } = useStore(
    useShallow(state => ({
      user: state.user,
      showSuccess: state.showSuccess,
      showError: state.showError,
      checkAuthStatus: state.checkAuthStatus,
    }))
  );

  // Agency management state
  const [isEditingAgency, setIsEditingAgency] = useState(false);
  const [isSavingAgency, setIsSavingAgency] = useState(false);
  const [agencyHnpUploadTab, setAgencyHnpUploadTab] = useState<
    HnpUploadTab | null | undefined
  >(user?.agency?.hnp_upload_tab);
  const [agencyHnpUploadTask, setAgencyHnpUploadTask] = useState<
    string | null | undefined
  >(user?.agency?.hnp_upload_task);
  const [isKinnserEnabled, setIsKinnserEnabled] = useState<boolean>(
    user?.agency?.is_kinnser_enabled ?? false
  );
  const [isRogerEmrEnabled, setIsRogerEmrEnabled] = useState<boolean>(
    user?.agency?.is_rogeremr_enabled ?? false
  );

  // Update agency fields when user changes
  useEffect(() => {
    setAgencyHnpUploadTab(user?.agency?.hnp_upload_tab);
    setAgencyHnpUploadTask(user?.agency?.hnp_upload_task);
    setIsKinnserEnabled(user?.agency?.is_kinnser_enabled ?? false);
    setIsRogerEmrEnabled(user?.agency?.is_rogeremr_enabled ?? false);
  }, [
    user?.agency?.hnp_upload_tab,
    user?.agency?.hnp_upload_task,
    user?.agency?.is_kinnser_enabled,
    user?.agency?.is_rogeremr_enabled,
  ]);

  // Handle agency edit
  const handleEditAgency = () => {
    setIsEditingAgency(true);
  };

  // Handle agency cancel edit
  const handleCancelAgencyEdit = () => {
    setIsEditingAgency(false);
    setAgencyHnpUploadTab(user?.agency?.hnp_upload_tab);
    setAgencyHnpUploadTask(user?.agency?.hnp_upload_task);
    setIsKinnserEnabled(user?.agency?.is_kinnser_enabled ?? false);
    setIsRogerEmrEnabled(user?.agency?.is_rogeremr_enabled ?? false);
  };

  // Handle agency save
  const handleSaveAgency = async () => {
    if (!user?.agency?.id) {
      showError('No agency found');
      return;
    }

    if (isKinnserEnabled) {
      const tabNameProvided =
        agencyHnpUploadTab !== null && agencyHnpUploadTab !== undefined;
      const taskNameProvided =
        agencyHnpUploadTask !== null &&
        agencyHnpUploadTask !== undefined &&
        agencyHnpUploadTask.trim() !== '';

      if (tabNameProvided !== taskNameProvided) {
        showError(
          'If H&P upload tab is specified, H&P upload task must also be specified, and vice versa.'
        );
        return;
      }
    }

    setIsSavingAgency(true);
    try {
      const hnpTab = isKinnserEnabled ? (agencyHnpUploadTab ?? null) : null;
      const hnpTask = isKinnserEnabled
        ? agencyHnpUploadTask?.trim() === '' || agencyHnpUploadTask === null
          ? null
          : (agencyHnpUploadTask ?? null)
        : null;

      await updateAgency({
        body: {
          hnp_upload_tab: hnpTab,
          hnp_upload_task: hnpTask,
          is_kinnser_enabled: isKinnserEnabled,
          is_rogeremr_enabled: isRogerEmrEnabled,
        },
      });
      showSuccess('Agency settings updated successfully');
      setIsEditingAgency(false);
      // Reload user data to get updated agency info
      await checkAuthStatus();
    } catch (error) {
      console.error('Failed to update agency:', error);
      showError('Failed to update agency settings');
    } finally {
      setIsSavingAgency(false);
    }
  };

  // Determine what to show based on mode and editing state
  const showDisplayView = mode === 'readonly' || !isEditingAgency;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Agency Management</CardTitle>
        <CardDescription>
          {mode === 'readonly'
            ? 'Feature settings for your agency'
            : 'Manage feature settings for your agency'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className='space-y-6'>
          {/* Kinnser Integration Section */}
          <div className='rounded-md border'>
            <div className='flex items-center justify-between p-4'>
              <div className='space-y-0.5'>
                <label
                  htmlFor='is_kinnser_enabled'
                  className='text-sm font-medium'
                >
                  Kinnser Integration
                </label>
                <p className='text-xs text-muted-foreground'>
                  Enable Kinnser features for this agency
                </p>
              </div>
              {!showDisplayView ? (
                <Switch
                  id='is_kinnser_enabled'
                  checked={isKinnserEnabled}
                  onCheckedChange={setIsKinnserEnabled}
                />
              ) : (
                <span
                  className={`text-sm font-medium ${isKinnserEnabled ? 'text-green-600' : 'text-muted-foreground'}`}
                >
                  {isKinnserEnabled ? 'Enabled' : 'Disabled'}
                </span>
              )}
            </div>
            {isKinnserEnabled && (
              <div className='grid gap-4 border-t bg-muted/30 p-4 md:grid-cols-2'>
                <div className='grid gap-2'>
                  <label
                    htmlFor='hnp_upload_tab'
                    className='text-sm font-medium'
                  >
                    H&P Upload Tab
                  </label>
                  {!showDisplayView ? (
                    <Select
                      value={agencyHnpUploadTab || 'none'}
                      onValueChange={value =>
                        setAgencyHnpUploadTab(
                          value === 'none' ? null : (value as HnpUploadTab)
                        )
                      }
                    >
                      <SelectTrigger id='hnp_upload_tab'>
                        <SelectValue placeholder='Select tab' />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value='none'>None</SelectItem>
                        {HNPUPLOADTAB_VALUES.map(tab => (
                          <SelectItem key={tab} value={tab}>
                            {tab.charAt(0).toUpperCase() + tab.slice(1)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <div className='flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm'>
                      {agencyHnpUploadTab ? (
                        <span className='capitalize'>{agencyHnpUploadTab}</span>
                      ) : (
                        <span>None</span>
                      )}
                    </div>
                  )}
                </div>
                <div className='grid gap-2'>
                  <label
                    htmlFor='hnp_upload_task'
                    className='text-sm font-medium'
                  >
                    H&P Upload Task
                  </label>
                  {!showDisplayView ? (
                    <Input
                      id='hnp_upload_task'
                      value={agencyHnpUploadTask || ''}
                      onChange={e =>
                        setAgencyHnpUploadTask(e.target.value || null)
                      }
                      placeholder='Enter task name'
                      maxLength={100}
                    />
                  ) : (
                    <div className='flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm'>
                      {agencyHnpUploadTask ? (
                        <span>{agencyHnpUploadTask}</span>
                      ) : (
                        <span className='text-muted-foreground'>Not set</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Roger EMR Section */}
          <div className='space-y-4'>
            <div className='flex items-center justify-between rounded-md border p-3'>
              <div className='space-y-0.5'>
                <label
                  htmlFor='is_rogeremr_enabled'
                  className='text-sm font-medium'
                >
                  Roger EMR
                </label>
                <p className='text-xs text-muted-foreground'>
                  Enable Roger EMR features for this agency
                </p>
              </div>
              {!showDisplayView ? (
                <Switch
                  id='is_rogeremr_enabled'
                  checked={isRogerEmrEnabled}
                  onCheckedChange={setIsRogerEmrEnabled}
                />
              ) : (
                <span
                  className={`text-sm font-medium ${isRogerEmrEnabled ? 'text-green-600' : 'text-muted-foreground'}`}
                >
                  {isRogerEmrEnabled ? 'Enabled' : 'Disabled'}
                </span>
              )}
            </div>
          </div>

          {mode === 'editable' && (
            <div className='flex justify-end gap-2'>
              {isEditingAgency ? (
                <>
                  <Button
                    variant='outline'
                    onClick={handleCancelAgencyEdit}
                    disabled={isSavingAgency}
                  >
                    Cancel
                  </Button>
                  <Button onClick={handleSaveAgency} disabled={isSavingAgency}>
                    {isSavingAgency ? (
                      <>
                        <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Check className='mr-2 h-4 w-4' />
                        Save Changes
                      </>
                    )}
                  </Button>
                </>
              ) : (
                <Button variant='outline' onClick={handleEditAgency}>
                  <Edit className='mr-2 h-4 w-4' />
                  Edit
                </Button>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default AgencyManagementSection;
